import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Users, ShieldCheck, Power, Crown } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function Admin() {
    const { user } = useAuth();
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);

    const load = async () => {
        try {
            const { data } = await api.get("/admin/users");
            setUsers(data);
        } catch {
            toast.error("No se pudieron cargar los usuarios");
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => {
        load();
    }, []);

    const patch = async (uid, body) => {
        try {
            await api.patch(`/admin/users/${uid}`, body);
            toast.success("Usuario actualizado");
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Error al actualizar");
        }
    };

    return (
        <div className="space-y-6" data-testid="admin-page">
            <div>
                <h1 className="font-head text-2xl">Administración</h1>
                <p className="text-sm text-muted-foreground">
                    Gestiona las cuentas de tus clientes: activa/desactiva accesos y roles.
                </p>
            </div>

            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                <Stat icon={Users} label="Usuarios" value={users.length} />
                <Stat icon={ShieldCheck} label="Activos" value={users.filter((u) => u.active).length} />
                <Stat icon={Crown} label="Admins" value={users.filter((u) => u.role === "admin").length} />
                <Stat icon={Power} label="Canales" value={users.reduce((a, u) => a + (u.channels || 0), 0)} />
            </div>

            <div className="overflow-x-auto border border-border">
                <table className="w-full min-w-[720px] text-left text-sm">
                    <thead className="bg-secondary text-[10px] uppercase tracking-widest text-muted-foreground">
                        <tr>
                            <th className="px-4 py-3">Usuario</th>
                            <th className="px-4 py-3">Rol</th>
                            <th className="px-4 py-3">Canales</th>
                            <th className="px-4 py-3">Publicados</th>
                            <th className="px-4 py-3">Estado</th>
                            <th className="px-4 py-3 text-right">Acciones</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading && (
                            <tr><td colSpan={6} className="px-4 py-6 text-center text-muted-foreground">Cargando…</td></tr>
                        )}
                        {!loading && users.map((u) => (
                            <tr key={u.user_id} className="border-t border-border" data-testid={`user-row-${u.user_id}`}>
                                <td className="px-4 py-3">
                                    <div className="flex items-center gap-2">
                                        {u.picture ? (
                                            <img src={u.picture} alt="" className="h-7 w-7 rounded-full" />
                                        ) : (
                                            <div className="flex h-7 w-7 items-center justify-center rounded-full bg-secondary text-[10px]">
                                                {(u.name || u.email || "?")[0].toUpperCase()}
                                            </div>
                                        )}
                                        <div>
                                            <div className="font-medium">{u.name}</div>
                                            <div className="text-[11px] text-muted-foreground">{u.email}</div>
                                        </div>
                                    </div>
                                </td>
                                <td className="px-4 py-3">
                                    <span className={`px-2 py-0.5 text-[10px] uppercase tracking-wider ${u.role === "admin" ? "bg-primary/20 text-primary" : "bg-secondary text-muted-foreground"}`}>
                                        {u.role}
                                    </span>
                                </td>
                                <td className="px-4 py-3">{u.channels}</td>
                                <td className="px-4 py-3">{u.publications}</td>
                                <td className="px-4 py-3">
                                    <span className={`inline-flex items-center gap-1.5 text-[11px] ${u.active ? "text-primary" : "text-destructive"}`}>
                                        <span className={`h-2 w-2 rounded-full ${u.active ? "bg-primary" : "bg-destructive"}`} />
                                        {u.active ? "Activo" : "Desactivado"}
                                    </span>
                                </td>
                                <td className="px-4 py-3">
                                    <div className="flex justify-end gap-2">
                                        <button
                                            data-testid={`toggle-active-${u.user_id}`}
                                            disabled={u.user_id === user?.user_id}
                                            onClick={() => patch(u.user_id, { active: !u.active })}
                                            className="border border-border px-2.5 py-1 text-[11px] uppercase tracking-wider transition-colors hover:border-primary hover:text-primary disabled:opacity-30"
                                        >
                                            {u.active ? "Desactivar" : "Activar"}
                                        </button>
                                        <button
                                            data-testid={`toggle-role-${u.user_id}`}
                                            disabled={u.user_id === user?.user_id}
                                            onClick={() => patch(u.user_id, { role: u.role === "admin" ? "user" : "admin" })}
                                            className="border border-border px-2.5 py-1 text-[11px] uppercase tracking-wider transition-colors hover:border-primary hover:text-primary disabled:opacity-30"
                                        >
                                            {u.role === "admin" ? "Hacer user" : "Hacer admin"}
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

function Stat({ icon: Icon, label, value }) {
    return (
        <div className="border border-border bg-secondary/40 p-4">
            <Icon size={16} className="text-primary" />
            <div className="mt-2 font-head text-2xl">{value}</div>
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{label}</div>
        </div>
    );
}
